/*
  Peter Augustus homepage | Shop the Range tabs
  13 Oct 2026 | Theme 138814587088

  Mounted on [data-pa-anim="range"] in blocks/_pa-range-tabs.liquid. Each Tab
  block renders only its panel (a block renders in one place), so the tab list
  and the Shop all links are built here from the panels' data-tab-label,
  data-all-url and data-all-label. Picking a tab shows its panel and its Shop
  all link and hides the others (WAI-ARIA tabs with arrow keys, Home and End).
  Picking a Tab block in the theme editor opens that tab. The section heading
  reveal is assets/pa-reveal.js.

  Switching tabs crossfades the cards (Calvin, 13 Oct). The current cards fade
  out, the new tab's photos finish loading (pointing at or focusing a tab
  starts that early), then the new cards fade up one after another. Reduced
  motion swaps straight away.
*/
(() => {
  if (!window.PAHome || !window.gsap) return;

  const CIRCLE = '<span class="btn__circle-wrap"><span class="btn__circle"><span class="before__100"></span></span></span><span class="btn__text"></span>';
  const pill = (tag, label) => {
    const el = document.createElement(tag);
    el.className = 'pa-btn pa-btn--outline pa-btn--sm pa-t-button';
    el.setAttribute('data-btn-hover', '');
    el.innerHTML = CIRCLE;
    el.querySelector('.btn__text').textContent = label || '';
    return el;
  };

  window.PAHome.register('range', (root, { designMode, reduceMotion }) => {
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    const tablist = root.querySelector('[data-range-tablist]');
    const allBox = root.querySelector('[data-range-all]');
    if (!panels.length || !tablist || !allBox) return undefined;

    // Keep the panel already on show when this runs again after a resize
    const current = (root.classList.contains('is-ready') && panels.find((panel) => !panel.hidden)) || panels[0];

    const tabs = panels.map((panel) => {
      const tab = pill('button', panel.dataset.tabLabel);
      tab.type = 'button';
      tab.classList.add('pa-shoprange__tab');
      tab.id = panel.id.replace('-panel-', '-tab-');
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', String(panel === current));
      tab.tabIndex = panel === current ? 0 : -1;
      tab.dataset.blockId = panel.dataset.blockId;
      panel.setAttribute('aria-labelledby', tab.id);
      panel.hidden = panel !== current;
      return tab;
    });
    tablist.replaceChildren(...tabs);

    const links = panels.filter((panel) => panel.dataset.allUrl).map((panel) => {
      const link = pill('a', panel.dataset.allLabel);
      link.classList.add('pa-shoprange__all-link');
      link.href = panel.dataset.allUrl;
      link.dataset.rangeFor = panel.id;
      link.hidden = panel !== current;
      return link;
    });
    allBox.replaceChildren(...links);
    root.classList.add('is-ready');

    const allCards = () => root.querySelectorAll('.pa-shoprange-card');
    const cardsOf = (panel) => panel.querySelectorAll('.pa-shoprange-card');
    const panelOf = (tab) => document.getElementById(tab.getAttribute('aria-controls'));
    let switchId = 0;
    let fadeOut = null;

    // Lazy photos in a hidden panel start loading once that tab is likely to be opened
    const warm = (panel) => panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });

    // Resolves when the panel's photos have loaded, or after 450ms so a slow photo never stalls the switch
    const photosReady = (panel) => {
      warm(panel);
      const pending = [...panel.querySelectorAll('img')].filter((img) => !img.complete);
      if (!pending.length) return Promise.resolve();
      return Promise.race([
        Promise.all(pending.map((img) => new Promise((done) => {
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }))),
        new Promise((done) => { setTimeout(done, 450); })
      ]);
    };

    const swap = (next) => {
      panels.forEach((panel) => { panel.hidden = panel !== next; });
      links.forEach((link) => { link.hidden = link.dataset.rangeFor !== next.id; });
      gsap.set(allCards(), { clearProps: 'opacity,visibility,transform' });
      const track = next.querySelector('.pa-shoprange__track');
      if (track) track.scrollLeft = 0;
    };

    const select = (tab, focus) => {
      tabs.forEach((t) => {
        t.setAttribute('aria-selected', String(t === tab));
        t.tabIndex = t === tab ? 0 : -1;
      });
      if (focus) tab.focus();

      const next = panelOf(tab);
      if (!next) return;
      const id = ++switchId; // any switch still waiting on its fade or photos is now stale
      if (fadeOut) fadeOut.kill();
      const prev = panels.find((panel) => !panel.hidden);

      if (next === prev) {
        // Back to the tab that is still on screen: bring its cards back
        gsap.to(cardsOf(next), { autoAlpha: 1, y: 0, duration: 0.3, overwrite: 'auto', clearProps: 'opacity,visibility,transform' });
        return;
      }

      if (reduceMotion || !prev) {
        gsap.killTweensOf(allCards());
        swap(next);
        return;
      }

      fadeOut = gsap.to(cardsOf(prev), { autoAlpha: 0, y: -8, duration: 0.25, stagger: 0.03, ease: 'power2.in', overwrite: 'auto' });
      Promise.all([fadeOut.then(() => {}), photosReady(next)]).then(() => {
        if (id !== switchId) return;
        swap(next);
        gsap.fromTo(cardsOf(next), { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.07, ease: 'power3.out', clearProps: 'opacity,visibility,transform' });
      });
    };

    const onClick = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && tab.getAttribute('aria-selected') !== 'true') select(tab);
    };

    const onKey = (event) => {
      const i = tabs.indexOf(event.target);
      if (i < 0) return;
      const last = tabs.length - 1;
      const next = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      select(tabs[next], true);
    };

    const onIntent = (event) => {
      const tab = event.target.closest('[role="tab"]');
      const panel = tab && panelOf(tab);
      if (panel) warm(panel);
    };

    // Tab blocks sit inside the Tabs group, so they are matched by block id as well as by target
    const onBlockSelect = (event) => {
      const id = event.detail && event.detail.blockId;
      const tab = tabs.find((t) => t.dataset.blockId === id) || (tabs.includes(event.target) ? event.target : null);
      if (tab) select(tab);
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKey);
    root.addEventListener('pointerover', onIntent);
    root.addEventListener('focusin', onIntent);
    if (designMode) document.addEventListener('shopify:block:select', onBlockSelect);

    return () => {
      switchId += 1;
      if (fadeOut) fadeOut.kill();
      gsap.killTweensOf(allCards());
      const current = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
      if (current && panelOf(current)) swap(panelOf(current));
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKey);
      root.removeEventListener('pointerover', onIntent);
      root.removeEventListener('focusin', onIntent);
      document.removeEventListener('shopify:block:select', onBlockSelect);
    };
  });
})();
